import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import type { ZeroResultSearchFilters } from "@/types";

function parseUrlToFilters(
  searchParams: URLSearchParams,
): ZeroResultSearchFilters {
  return {
    status: (searchParams.get("status") as ZeroResultSearchFilters["status"]) || undefined,
    minSearchCount: searchParams.get("minSearchCount")
      ? parseInt(searchParams.get("minSearchCount")!, 10)
      : undefined,
    minUniqueUsers: searchParams.get("minUniqueUsers")
      ? parseInt(searchParams.get("minUniqueUsers")!, 10)
      : undefined,
    searchTerm: searchParams.get("searchTerm") || undefined,
    dateFrom: searchParams.get("dateFrom") || undefined,
    dateTo: searchParams.get("dateTo") || undefined,
    sort: searchParams.get("sort") || "totalSearchCount",
    sortOrder:
      (searchParams.get("sortOrder") as "ASC" | "DESC") || "DESC",
    page: searchParams.get("page") ? parseInt(searchParams.get("page")!, 10) : 1,
    limit: searchParams.get("limit")
      ? parseInt(searchParams.get("limit")!, 10)
      : 10,
  };
}

function filtersToUrl(filters: ZeroResultSearchFilters): URLSearchParams {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    params.set(key, String(value));
  });

  return params;
}

export function useZeroResultSearchFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(
    () => parseUrlToFilters(searchParams),
    [searchParams],
  );

  const setFilter = useCallback(
    <K extends keyof ZeroResultSearchFilters>(
      key: K,
      value: ZeroResultSearchFilters[K],
    ) => {
      const newFilters = { ...parseUrlToFilters(searchParams), [key]: value };
      if (key !== "page" && key !== "limit" && key !== "sort" && key !== "sortOrder") {
        newFilters.page = 1;
      }
      setSearchParams(filtersToUrl(newFilters), { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const setFilters = useCallback(
    (newFilters: Partial<ZeroResultSearchFilters>) => {
      const currentFilters = parseUrlToFilters(searchParams);
      const mergedFilters = { ...currentFilters, ...newFilters };
      const hasFilterChange = Object.keys(newFilters).some(
        (key) =>
          key !== "page" && key !== "limit" && key !== "sort" && key !== "sortOrder",
      );
      if (hasFilterChange && !("page" in newFilters)) {
        mergedFilters.page = 1;
      }
      setSearchParams(filtersToUrl(mergedFilters), { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setSearchParams(
      filtersToUrl({
        page: 1,
        limit: filters.limit,
        sort: "totalSearchCount",
        sortOrder: "DESC",
      }),
      { replace: true },
    );
  }, [setSearchParams, filters.limit]);

  return { filters, setFilter, setFilters, clearFilters };
}
